import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

/**
 * Horizontaler Filter-Container fuer Seiten wie Tickets, Artikel, Vertraege.
 * Rendert eine weisse Card mit flexiblem Kinder-Layout.
 */
export const FilterBar = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-white border border-gray-200 rounded-lg shadow-card px-3 py-2 flex items-center gap-2 flex-wrap',
        className,
      )}
      {...props}
    />
  ),
)
FilterBar.displayName = 'FilterBar'

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
  leadingIcon?: ReactNode
  count?: number | null
  onRemove?: () => void
}

/**
 * Anklickbarer Filter-Chip. `active` hebt den ausgewaehlten Zustand hervor.
 * Optional `onRemove` fuer ein X rechts (aktiver Filter).
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    { className, active, leadingIcon, count, children, onRemove, type = 'button', ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs leading-tight transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
        active
          ? 'bg-brand-400 border-brand-400 text-white hover:bg-brand-500'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
        className,
      )}
      {...props}
    >
      {leadingIcon ? <span className="[&>svg]:h-3 [&>svg]:w-3">{leadingIcon}</span> : null}
      <span>{children}</span>
      {typeof count === 'number' ? (
        <span
          className={cn(
            'rounded-full px-1.5 text-caption',
            active ? 'bg-brand-700/40 text-white' : 'bg-gray-100 text-gray-600',
          )}
        >
          {count}
        </span>
      ) : null}
      {onRemove ? (
        <span
          role="button"
          aria-label="Filter entfernen"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className={cn(
            'rounded-full p-0.5 -mr-0.5',
            active ? 'hover:bg-brand-700/40' : 'hover:bg-gray-200',
          )}
        >
          <X className="h-3 w-3" />
        </span>
      ) : null}
    </button>
  ),
)
FilterChip.displayName = 'FilterChip'
